/**
 * PATH: packages/ui/src/components/email/RecipientList.tsx
 * FILE_ID: UI-EMAIL-RECIPIENTLIST-001
 * PURPOSE: Render an email's To/Cc/Bcc list so each recipient can REVEAL
 *          its underlying address on tap (mobile) / hover-title (desktop),
 *          with a "+N more" overflow toggle for long lists.
 * WHY:     Operator (2026-06-16): "I was CC'ed, I'm trying to click my name
 *          to see which email it CC'd to, yet I can't." Recipients were
 *          rendered as a flat name-only string (`formatRecipientList`), which
 *          hid the address entirely — and with multiple connected mailboxes
 *          (abhishek@finsoeasy.com + abhishek.sehgal@Bravada.com.au) the
 *          operator couldn't tell which of his addresses actually received
 *          the mail. This is the one shared renderer for every reader surface
 *          (full reader, preview pane, stacked thread message).
 * FLOW:
 *   ┌────────────────┐   ┌────────────────────┐   ┌──────────────────────┐
 *   │ recipients[]   │ → │ buildRecipientViews │ → │ name chips +          │
 *   │ {name,email}   │   │ (pure, drops empty) │   │ tap → "<email>" reveal│
 *   └────────────────┘   └────────────────────┘   └──────────────────────┘
 * RELATED:
 *   - ./recipients (getRecipientEmail / formatRecipientName — pure helpers)
 *   - email-hub ThreadMessage / EmailPreviewPane, EmailReader (consumers)
 */

import { useState } from 'react'

import { cn } from '../../lib/utils'
import { EMAIL_ADDRESS_ATTR } from './addressTarget'
import type { EmailRecipient } from './types'
import { formatRecipientName, getRecipientEmail } from './recipients'

/** Render-ready view of one recipient. */
export interface RecipientView {
  /** Best display label — name when present, else the address. */
  label: string
  /** Canonical address; '' when the payload carried none. */
  email: string
  /** True when the address adds information beyond the label (i.e. there
   *  is an address AND it differs from the label) — only then is the chip
   *  worth making interactive to reveal it. */
  revealable: boolean
}

/** Pure: map raw recipients (bare strings OR `{name,email}` objects) to
 *  render-ready views, dropping entries that carry neither a name nor an
 *  address. Input → output, no side effects (unit-tested). */
export function buildRecipientViews(
  recipients: EmailRecipient[] | null | undefined,
): RecipientView[] {
  if (!recipients || recipients.length === 0) return []
  const views: RecipientView[] = []
  for (const r of recipients) {
    const label = formatRecipientName(r)
    if (!label) continue
    const email = getRecipientEmail(r)
    views.push({ label, email, revealable: Boolean(email) && email !== label })
  }
  return views
}

export interface RecipientListProps {
  recipients: EmailRecipient[] | null | undefined
  /** Leading label, e.g. 'To' / 'Cc' / 'Bcc' → renders 'Cc: …'. */
  label?: string
  /** Shown when the list resolves empty (e.g. 'me'). Omit → render
   *  nothing for an empty list. */
  emptyText?: string
  /** Collapse the list after N names behind a '+N more' toggle. */
  max?: number
  className?: string
}

/** Inline, reveal-on-tap recipient list. Renders a `<span>` so it drops
 *  into an existing text line (e.g. "To: … · 16 Jun"). Each chip with a
 *  distinct display name becomes a button that toggles its `<email>`;
 *  bare-address recipients (nothing extra to reveal) render as plain text.
 *  Every click `stopPropagation`s so the list is safe inside a clickable
 *  row without triggering the row's own handler. */
export function RecipientList({
  recipients,
  label,
  emptyText,
  max,
  className,
}: RecipientListProps) {
  const views = buildRecipientViews(recipients)
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set<number>())
  const [expanded, setExpanded] = useState(false)

  if (views.length === 0) {
    if (!emptyText) return null
    return (
      <span className={className}>
        {label ? `${label}: ` : ''}
        {emptyText}
      </span>
    )
  }

  const limited = Boolean(max) && !expanded && views.length > (max as number)
  const shown = limited ? views.slice(0, max as number) : views
  const overflow = limited ? views.length - (max as number) : 0

  const toggle = (i: number) =>
    setRevealed((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })

  return (
    <span className={className}>
      {label ? `${label}: ` : ''}
      {shown.map((v, i) => {
        const isRevealed = revealed.has(i)
        const display = isRevealed && v.email ? `${v.label} <${v.email}>` : v.label
        const addressProps = v.email.trim()
          ? { [EMAIL_ADDRESS_ATTR]: v.email.trim() }
          : {}
        return (
          <span key={i}>
            {i > 0 && ', '}
            {v.revealable ? (
              <button
                type="button"
                {...addressProps}
                onClick={(e) => {
                  e.stopPropagation()
                  toggle(i)
                }}
                title={`${v.label} <${v.email}>`}
                aria-label={
                  isRevealed
                    ? `${v.label} ${v.email}, hide address`
                    : `${v.label}, show address`
                }
                aria-expanded={isRevealed}
                className={cn(
                  'rounded-sm underline decoration-dotted decoration-muted-foreground/40',
                  'underline-offset-2 hover:decoration-foreground',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                {display}
              </button>
            ) : (
              <span {...addressProps} title={v.email || undefined}>{display}</span>
            )}
          </span>
        )
      })}
      {overflow > 0 && (
        <>
          {', '}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(true)
            }}
            className={cn(
              'rounded-sm underline decoration-dotted decoration-muted-foreground/40 underline-offset-2',
              'hover:decoration-foreground',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            + {overflow} more
          </button>
        </>
      )}
    </span>
  )
}
